import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { forgotPasswordSchema } from './validations';
import { forgotPasswordApi } from '../../services/authService';
import { whiteLogo } from '../../assets';
import { ErrorText } from '../../components';
import { Logs } from '../../services';
import { notification } from 'antd';

const primaryBlue = "#0e3584";

export const ForgotPassword = ({ handleBackLogin }) => {
  const [apiResponseError, setApiResponseError] = useState(false);
  const form = useForm({
    resolver: yupResolver(forgotPasswordSchema),
    mode: 'all',
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const handleForgotPasswordClick = async (data) => {
    try {
      const email = data?.email.toLowerCase();
      const res = await forgotPasswordApi(email);
      if (res.status === 200){
        notification.success({
          message: "Password reset link sent!",
          description: "Check Your Spam Folder If You Don't See It In Your Inbox."
        })
      }
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        'Something went wrong. Please try again.';

      notification.error({
        message: 'Request failed',
        description: apiMessage,
      });

      setApiResponseError(apiMessage);
      Logs(error);
    }
  };

  const handleEmailChange = (event) => {
    setApiResponseError(null); 
    form.setValue('email', event.target.value);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        bgcolor: primaryBlue,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        m: 0,
      }}
    >
      {/* Left panel */}
      <Box
        sx={{
          width: { xs: "0", md: "50%" },
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          pl: { md: 12, lg: 20 },
          color: "#fff",
          height: "100vh",
          bgcolor: primaryBlue,
        }}
      >
        <Box component="img" src={whiteLogo} sx={{ width: 240, mb: 2 }} />
        <Typography sx={{ fontSize: 18, opacity: 0.9, mb: 4, color: "#fff" }}>
          Unified Verification for Modern Lending. Access APIs with confidence – compliant, secure, and fraud-free.
        </Typography>
        <Stack spacing={2}>
        <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          • Compliance-first onboarding
        </Typography>
        <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          • Faster fraud detection
        </Typography>
        <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          • Access multiple APIs seamlessly
        </Typography>
        <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          • Trusted by leading BFSIs
        </Typography>
      </Stack>
      </Box>

      {/* Right panel - Forgot Password form */}
      <Box
        sx={{
          width: { xs: "100%", sm: "90%", md: "50%", lg: "35%" },
          minHeight: { xs: "100vh", md: "auto" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "transparent",
        }}
      >
        <Paper
          elevation={4}
          sx={{
            borderRadius: "20px",
            px: { xs: 2, sm: 8 },
            py: { xs: 4, sm: 8 },
            width: "100%",
            maxWidth: 400,
            boxSizing: "border-box",
            minHeight: 400,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography variant="h4" fontWeight={600} textAlign="center" mb={2}>
            Reset your password
          </Typography>
          <Typography variant="body1" textAlign="center" mb={4} color="text.secondary">
            Enter your email address and we'll send you a link to reset your password.
          </Typography>
          
          <form onSubmit={handleSubmit(handleForgotPasswordClick)} autoComplete="off">
            <Stack spacing={3}>
              {apiResponseError && (
                <ErrorText>{apiResponseError}</ErrorText>
              )}
              <TextField
                fullWidth
                label="Email address"
                id="email"
                type="email"
                placeholder="Enter your email"
                {...register('email')}
                onChange={handleEmailChange}
                error={!!errors.email}
                helperText={errors.email?.message}
                InputLabelProps={{ shrink: true }}
                InputProps={{ style: { fontSize: 22 } }}
                autoComplete="email"
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ fontSize: 18, fontWeight: 600, py: 1.5, mt: 1 }}
              >
                Send reset link
              </Button>
              <Button
                sx={{ alignSelf: "center", textTransform: "none" }}
                onClick={handleBackLogin}
                size="small"
              >
                Back to Sign In
              </Button>
            </Stack>
          </form>
          
          <Box mt={6} textAlign="center">
            <Typography variant="body2" color="text.secondary" fontSize={16}>
              powered by{" "}
              <a
                href="https://www.habilelabs.io/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#2ab6e5", fontWeight: 500, textDecoration: "none" }}
              >
                HabileLabs
              </a>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

ForgotPassword.propTypes = {
  handleBackLogin: PropTypes.func,
};